/**
 * Typed API call functions. All return typed response data or throw ApiError.
 */

import axios from "axios";
import { apiClient } from "./client";
import type {
  ConversionOptions,
  ConversionResult,
  JobCreated,
  JobStatusResponse,
} from "./types";

/**
 * Synchronous conversion — returns Markdown result directly.
 * Used for files <= 5 MB.
 *
 * @param file      PDF file to convert.
 * @param options   Conversion options.
 * @param onProgress  Upload progress callback (0–100).
 */
export async function convertSync(
  file: File,
  options: ConversionOptions,
  onProgress?: (percent: number) => void
): Promise<ConversionResult> {
  const form = new FormData();
  form.append("file", file);
  form.append("options", JSON.stringify(options));

  const response = await apiClient.post<ConversionResult>("/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (event) => {
      if (event.total && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });

  return response.data;
}

/**
 * Async conversion — enqueues a background job and returns a job_id to poll.
 * Used for files > 5 MB.
 */
export async function convertAsync(
  file: File,
  options: ConversionOptions,
  onProgress?: (percent: number) => void
): Promise<JobCreated> {
  const form = new FormData();
  form.append("file", file);
  form.append("options", JSON.stringify(options));

  const response = await apiClient.post<JobCreated>("/jobs", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (event) => {
      if (event.total && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });

  return response.data;
}

interface PresignedUploadResponse {
  job_id: string;
  upload_url: string;
  expires_in: number;
}

/**
 * Get a presigned S3 PUT URL for direct browser upload.
 * Use this for files that exceed Lambda's 6 MB payload limit.
 */
export async function getPresignedUploadUrl(): Promise<PresignedUploadResponse> {
  const response = await apiClient.post<PresignedUploadResponse>("/jobs/presigned-upload");
  return response.data;
}

/**
 * Upload a file directly to S3 using a presigned PUT URL.
 * Bypasses Lambda entirely — no size limit beyond S3's 5 GB single-PUT limit.
 */
export async function uploadToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", "application/pdf");
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }
    xhr.onload = () => (xhr.status === 200 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status}`)));
    xhr.onerror = () => reject(new Error("S3 upload network error"));
    xhr.send(file);
  });
}

/**
 * Enqueue a conversion job for a file already uploaded directly to S3.
 */
export async function enqueueJob(
  jobId: string,
  filename: string,
  options: ConversionOptions
): Promise<JobCreated> {
  const response = await apiClient.post<JobCreated>("/jobs/enqueue", {
    job_id: jobId,
    filename,
    options,
  });
  return response.data;
}

/** Poll job status. */
export async function getJobStatus(jobId: string): Promise<JobStatusResponse> {
  const response = await apiClient.get<JobStatusResponse>(`/jobs/${jobId}`);
  return response.data;
}

/**
 * Fetch a conversion result directly from a presigned S3 URL.
 * Used when the result was too large to inline in the poll response
 * (bypasses Lambda's 6 MB response limit). Uses a bare request — the URL is
 * absolute and pre-authenticated, so it must not go through apiClient's baseURL.
 */
export async function fetchResultFromUrl(url: string): Promise<ConversionResult> {
  const response = await axios.get<ConversionResult>(url, { timeout: 120_000 });
  return response.data;
}

/** Cancel a pending job. */
export async function cancelJob(jobId: string): Promise<void> {
  await apiClient.delete(`/jobs/${jobId}`);
}
